// ---------------------------------------------------------------------------
// Search Fallback — word-by-word + typo-tolerance
//
// When a search returns 0 results:
//   Full-page (/search):       shows products matching individual query words
//   Predictive (dropdown):     appends related products below the empty results
//
// Typo correction: if a word has no results, we progressively shorten it
// (e.g. "journallingg" → "journalling" → … → "journall" → finds results).
// ---------------------------------------------------------------------------
(function () {
  'use strict';

  function safeSetHTML(el, html) {
    if (!el) return;
    el.replaceChildren(document.createRange().createContextualFragment(html));
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  const STOP_WORDS = new Set([
    'the',
    'and',
    'for',
    'with',
    'from',
    'that',
    'this',
    'are',
    'was',
    'has',
    'not',
    'but',
    'its',
    'into',
    'over',
    'under',
    'after',
  ]);
  const MIN_WORD_LEN = 3;
  const SUGGEST_LIMIT = 6;
  const MAX_PAGE_RESULTS = 12;
  const MAX_DROPDOWN_RESULTS = 5;
  const FETCH_TIMEOUT_MS = 4000;
  const MIN_TYPO_WORD_LEN = 5;
  const MIN_PREFIX_RATIO = 0.5; // try prefixes down to 50% of word length (was 70%)
  const MIN_PREFIX_LEN = 4;

  // Known misspellings / British spellings for craft-store vocabulary.
  // Applied before any API call so the corrected term appears in the results label.
  const WORD_CORRECTIONS = {
    // British spellings
    stationary: 'stationery', // "not moving" vs writing supplies — very common error
    watercolour: 'watercolor',
    watercolours: 'watercolors',
    jewellery: 'jewelry',
    favourite: 'favorite',
    favourites: 'favorites',
    colour: 'color',
    colours: 'colors',
    organise: 'organize',
    organiser: 'organizer',
    // Craft-store typos observed in zero-result analytics
    beastiar: 'bestiary',
    beastera: 'bestiary',
    calender: 'calendar', // "advent calender" (very common)
    cath: 'cathedral', // "cath tape loop" → Cathedral Bloom Tape Loop
    cathd: 'cathedral',
    cathed: 'cathedral',
    cathid: 'cathedral',
    cletoc: 'celtic', // "cletoc" brand typo → Celtic products
    emphermer: 'ephemera',
    joirnal: 'journal',
    journalcover: 'folio', // "journal cover" run together
    poison: 'potion', // apothecary/craft theme; store has Potions products
    poission: 'potion',
    poisso: 'potion',
    refillabjournal: 'journal refillable',
    scbox: 'scotland', // ScotlandBox brand → Scotland-themed products
    scotbox: 'scotland',
    scotlanndbox: 'scotland',
    sentimemts: 'sentiments',
    sentim: 'sentiments',
    smarkan: 'stationery', // unknown brand → generic fallback
    smarkl: 'stationery',
    soolla: 'organizer pouch', // Soolla brand (tag indexed async) → category fallback
    washing: 'washi', // "half inch washing tape" → washi tape
    // Zero-result analytics 2026-05 — character-level typos
    airies: 'fairies', // "$airies" — $ stripped to "airies" → fairies
    velum: 'vellum', // vellum paper
    fanrasy: 'fantasy',
    faiey: 'fairy',
    tspe: 'tape', // "transparent tspe", "hills transparent tspe"
    acetin: 'acetate', // acetate/transparency film
    rouyal: 'royal',
    cutrinf: 'cutting',
    '0alice': 'alice', // "0alice" — leading-zero typo
    iks: 'inks', // from "powder i ks" → word "iks"
    aiviat: 'aviation',
    tacj: 'tack', // adhesive tack
    // Scotland progressive misspellings ("sotttland", "sott", "sot", etc.)
    sot: 'scotland',
    sott: 'scotland',
    sottland: 'scotland',
    sotttland: 'scotland',
    sotttla: 'scotland',
  };

  // Full-query overrides: applied to the normalized query BEFORE word splitting.
  // Key = lowercased trimmed query; value = replacement query to split and resolve instead.
  const QUERY_OVERRIDES = {
    // Acrylic stamp tools
    acrylic: 'stamp block acrylic',
    'acrylic stamp': 'stamp block',
    'acrylic stamp block': 'stamp block',
    'stamp acrylic': 'stamp block',
    // Washi tape sizing searches
    'half inch': 'washi tape',
    'half inch washi': 'washi tape',
    'half inch washi tape': 'washi tape',
    'half inch washing tape': 'washi tape',
    // Floral / sticker
    'floral sticker': 'sticker floral',
    'floral stickers': 'sticker floral',
    'floral stick': 'floral sticker',
    'pink flower': 'floral botanical pink',
    // Journal accessories
    'journal cover': 'folio journal cover',
    'journal covers': 'folio journal cover',
    // Calendars / planners
    'advent calendar': 'calendar',
    'advent calender': 'calendar',
    'mid year calendar': 'calendar',
    'mid-year calendar': 'calendar',
    midyear: 'calendar',
    // Blank notebooks
    'blank journal': 'journal notebook',
    'blank journals': 'journal notebook',
    'blank note': 'notebook journal',
    // Stamps
    sentiments: 'stamp', // no text/sentiment stamps in catalog → show stamps
    'compass stamp': 'navigation stamp compass',
    'compass stamps': 'navigation stamp compass',
    // Snail mail / pen pal
    'snail mail': 'ephemera sticker letter', // "mail" alone returns 0
    // Foil / glimmer products
    'wild wear': 'sticker washi',
    'glimmer plates': 'foil stamp',
    'glimmer plate': 'foil stamp',
    'hot foil': 'foil stamp',
    'hot foil plates': 'foil stamp',
    'flame resi': 'flames washi', // "flame resi[n/stance]" → Flames Cutout Washi Tape
    // Geographic searches (no NZ/buffalo products; show adjacent nature/travel items)
    'new zealand': 'celtic ireland travel nature',
    'new zeala': 'celtic ireland nature',
    'new zea': 'celtic nature',
    // Wildlife / nature (no Galapagos products; show wildlife stickers)
    galapagos: 'wildlife sticker tropical',
    'galapagos sticker': 'wildlife sticker',
    'galapagos sti': 'wildlife sticker',
    // Poetic Spring products (multi-word searches for specific collection)
    'poetic tape': 'poetic spring',
    'poetic spring sticker book': 'poetic spring sticker',
    // Adhesive supplies
    adhesive: 'washi tape paste',
    // Miscellaneous typos / spacing
    'stick s heet': 'sticker sheet',
    tiny: 'tiny sticker fairy',
    'tiny wee': 'tiny sticker fairy',
    // Brands not in catalog → fallback to category
    'amethyst pearl': 'alchemy amethyst pearl',
    // ── Compound words (run together without space) — 2026-05 zero-result data
    diecut: 'die cut',
    washitape: 'washi tape',
    stickerbook: 'sticker book',
    sticbook: 'sticker book',
    ephemerabook: 'ephemera book',
    lilypad: 'lily',
    rubons: 'rub on transfer',
    // ── Special-character / non-Latin queries
    $airies: 'fairy sticker ephemera', // SHIFT+4 misfire instead of F; $ stripped per char-clean
    шкудфтв: 'ireland celtic', // "ireland" typed with Russian/Cyrillic keyboard layout active
    // ── Craft-theme searches with no direct product name match
    sewing: 'sewing stitch needle craft',
    'sewing washi': 'sewing washi tape',
    'sewing washi ta': 'sewing washi tape',
    marigold: 'marigold floral botanical',
    sword: 'fantasy medieval nature',
    dagger: 'fantasy medieval',
    'flower stickers or ephemera': 'floral sticker ephemera',
    april: 'spring floral botanical',
    advent: 'advent calendar christmas',
    baby: 'baby shower newborn',
    beach: 'coastal ocean nature',
    glue: 'tape adhesive craft',
    'washi frog': 'frog washi tape',
    waterfall: 'nature water botanical',
    rainbow: 'rainbow colorful sticker',
    mandala: 'mandala botanical floral',
    elf: 'fairy fantasy',
    rain: 'nature botanical',
    week: 'weekly planner stationery',
    home: 'home decor craft sticker',
    'diamond art': 'diamond painting gem',
    aviation: 'aviation airplane travel',
    airplane: 'aviation airplane travel',
    ireland: 'ireland celtic nature',
    levenger: 'planner journal notebook', // brand not in catalog → category
    tack: 'tape adhesive craft', // adhesive tack → craft adhesives
    // ── Geographic searches
    iceland: 'iceland nordic nature',
    icel: 'iceland nature',
    'i el d': 'iceland nature',
    'i el': 'iceland nature',
    // ── Partial / typo product and collection name searches
    'book l ers': 'book lovers',
    'book l er': 'book lovers',
    'vcb march 2024': 'vintage craft box',
    'vcb march': 'vintage craft box',
    'jane au': 'jane austen',
    'jane austi': 'jane austen',
    'jane aust': 'jane austen',
    '365 daily': 'planner daily stationery',
    '365 daily sta': 'planner stationery',
    'antiquity organizer': 'organizer vintage',
    'artist trading': 'artist trading card',
    'artist trading ca': 'artist trading card',
    'artist trading cards': 'artist trading card ephemera',
    'may vintage': 'vintage craft box',
    'may vintage craft box upgrades': 'vintage craft box upgrade',
    'may vintage craft box up': 'vintage craft box upgrade',
    'may vint': 'vintage craft box',
    'floral book': 'floral book lovers sticker',
    'floral book lovers': 'book lovers floral sticker',
    'alice in wonderland box': 'alice wonderland sticker',
    aliices: 'alice wonderland',
    'aliices tes': 'alice wonderland tea',
    '0alice': 'alice wonderland',
    'venice carnival papers': 'venice carnival sticker',
    'venice carnival papers (a5)': 'venice carnival sticker',
    'the new look washi': 'new look washi tape',
    'flowered nostalgia': 'floral nostalgia vintage',
    'academia 35': 'academia vinyl sticker',
    'academia 35 viny': 'academia vinyl sticker',
    'academia 35 vinyl': 'academia vinyl sticker',
    'dark academia stamp': 'dark academia botanical',
    'magic of the stars': 'stars celestial magical sticker',
    'ancient lore celtic': 'celtic ancient nature',
    classica: 'classic botanical vintage',
    'steampunk box': 'steampunk vintage craft',
    'silk road foam stamps': 'silk road stamp',
    'silk road foam stamp': 'silk road stamp',
    'trinity of': 'trinity',
    'vintage collector stick book': 'sticker book vintage',
    'vintage collector stick': 'sticker book vintage',
    'pet loops sindhuru': 'pet loops craft',
    'pet loops sindhu': 'pet loops craft',
    'pet loops craft': 'craft loop',
    'misty mountatransparent tspe': 'misty mountain transparent tape',
    'misty mountaintransparent tspe': 'misty mountain transparent tape',
    'hills transparent tspe': 'hills transparent tape',
    'transparent tspe': 'transparent tape',
    'washitape set': 'washi tape set',
    '4 elwments': 'elements nature',
    'powder i ks': 'powder inks',
    'art aticke': 'art sticker',
    'tiny wee things': 'tiny sticker fairy',
    // ── Short-prefix / early-typing fallbacks (2–3 chars; native search returns nothing)
    st: 'stamps stickers stationery',
    sti: 'stickers stationery',
    // ── Design style searches (product tagged but not in title)
    'art deco': 'notepads art deco',
    'art deco tape': 'notepads art deco',
    'art deco washi': 'notepads art deco',
  };

  function splitQuery(q) {
    const norm = q.toLowerCase().trim();
    // Apply full-query override before splitting
    const overridden = QUERY_OVERRIDES[norm];
    const effective = overridden || norm;

    return effective
      .split(/[\s+\-_]+/) // also split on hyphens/underscores ("bullet-journal" → 2 words)
      .map(function (w) {
        return w.replace(/[^a-z0-9]/g, ''); // strip apostrophes, dots, etc. ("craft's" → "crafts")
      })
      .filter(function (w) {
        return w.length >= MIN_WORD_LEN && !STOP_WORDS.has(w);
      })
      .filter(function (w, i, arr) {
        return arr.indexOf(w) === i; // dedupe (corrections map can create duplicates)
      })
      .map(function (w) {
        return WORD_CORRECTIONS[w] || w;
      });
  }

  // Session-scoped cache for resolveWord results — avoids repeat API calls on
  // back-navigation and re-typed zero-result queries within the same tab.
  const _wordCache = new Map();

  // Fetch up to `limit` products matching `word` via Shopify's suggest API.
  // Pass `fields` to restrict which product fields are searched (default: all).
  async function suggestProducts(word, limit, fields) {
    try {
      const params = new URLSearchParams({
        q: word,
        'resources[type]': 'product',
        'resources[limit]': limit || SUGGEST_LIMIT,
        'resources[options][fields]': fields || 'title,body,vendor,product_type,tag',
      });
      const resp = await fetch('/search/suggest.json?' + params.toString(), {
        signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
      });
      if (!resp.ok) return [];
      const data = await resp.json();
      return (data.resources && data.resources.results && data.resources.results.products) || [];
    } catch (_e) {
      return [];
    }
  }

  // For a single word: try exact → de-double → prefix shrink.
  // Returns { term, products } where term is the corrected word that found results.
  async function resolveWord(word) {
    if (_wordCache.has(word)) return _wordCache.get(word);

    let products = await suggestProducts(word, SUGGEST_LIMIT);
    if (products.length > 0) {
      const hit = { term: word, products: products };
      _wordCache.set(word, hit);
      return hit;
    }

    if (word.length <= MIN_TYPO_WORD_LEN) {
      _wordCache.set(word, { term: word, products: [] });
      return _wordCache.get(word);
    }

    // Strip duplicate consecutive consonants (e.g. "plannner"→"planner", "journalling"→"journaling").
    // One extra API call, zero prefix iterations if it hits.
    const deduped = word.replace(/([bcdfghjklmnpqrstvwxyz])\1+/gi, '$1');
    if (deduped !== word) {
      products = await suggestProducts(deduped, SUGGEST_LIMIT);
      if (products.length > 0) {
        const dedupedHit = { term: deduped, products: products };
        _wordCache.set(word, dedupedHit);
        return dedupedHit;
      }
    }

    // Fire all prefix lengths in parallel, then pick the SHORTEST that gets results.
    // Sequential stopping at the first (longest) hit caused labels like "washit"
    // instead of "washi" — the shorter prefix is the cleaner display term.
    const minLen = Math.max(MIN_PREFIX_LEN, Math.ceil(word.length * MIN_PREFIX_RATIO));
    const lens = [];
    for (let l = word.length - 1; l >= minLen; l--) lens.push(l);

    const prefixResults = await Promise.all(
      lens.map(function (l) {
        return suggestProducts(word.slice(0, l), SUGGEST_LIMIT);
      })
    );

    // lens is ordered longest→shortest; walk backwards to find shortest hit
    for (let i = lens.length - 1; i >= 0; i--) {
      if (prefixResults[i].length > 0) {
        const prefixHit = { term: word.slice(0, lens[i]), products: prefixResults[i] };
        _wordCache.set(word, prefixHit);
        return prefixHit;
      }
    }

    const miss = { term: word, products: [] };
    _wordCache.set(word, miss);
    return miss;
  }

  // Resolve all words in parallel, collect unique products, return matched terms.
  async function resolveQuery(words) {
    const resolutions = await Promise.all(words.map(resolveWord));
    const productMap = new Map();
    const matched = [];
    resolutions.forEach(function (resolved) {
      if (resolved.products.length > 0) {
        matched.push(resolved.term);
        resolved.products.forEach(function (p) {
          if (!productMap.has(p.handle)) productMap.set(p.handle, p);
        });
      }
    });
    return {
      products: Array.from(productMap.values()).slice(0, MAX_PAGE_RESULTS),
      matched: matched,
    };
  }

  function formatPrice(p) {
    const n = parseFloat(p.price);
    return isNaN(n) ? '' : '$' + n.toFixed(2);
  }

  // ── Full-page search — grid card ─────────────────────────────────────────

  function buildPageCard(product) {
    const li = document.createElement('li');
    li.className = 'f-column';
    const imgHtml = product.image
      ? '<img src="' +
        product.image +
        '" alt="" loading="lazy" style="width:100%;aspect-ratio:1/1;object-fit:cover;border-radius:var(--radius-product-card,8px);">'
      : '<div style="width:100%;aspect-ratio:1/1;background:rgba(255,255,255,0.06);border-radius:8px;"></div>';
    const price = formatPrice(product);
    safeSetHTML(
      li,
      '<a href="' +
        escapeHtml(product.url) +
        '" style="display:block;text-decoration:none;color:inherit;">' +
        '<div style="margin-bottom:8px;">' +
        imgHtml +
        '</div>' +
        '<p class="m-0" style="font-size:0.88em;line-height:1.3;">' +
        escapeHtml(product.title) +
        '</p>' +
        (price
          ? '<p class="m-0" style="font-size:0.9em;font-weight:600;margin-top:3px;">' +
            price +
            '</p>'
          : '') +
        '</a>'
    );
    return li;
  }

  // ── Predictive dropdown — list item matching theme card structure ─────────

  function buildDropdownItem(product, idx) {
    const li = document.createElement('li');
    li.id = 'search-fallback-option-' + idx;
    li.className = 'predictive-search__list-item';
    li.setAttribute('role', 'option');
    li.setAttribute('aria-selected', 'false');
    const imgHtml = product.image
      ? '<a href="' +
        escapeHtml(product.url) +
        '" class="media-wrapper blocks-radius-sm" tabindex="-1">' +
        '<img src="' +
        product.image +
        '" alt="" loading="lazy" width="60" height="60" style="width:60px;height:60px;object-fit:cover;">' +
        '</a>'
      : '';
    const price = formatPrice(product);
    safeSetHTML(
      li,
      '<div class="flex items-center gap-3">' +
        imgHtml +
        '<div class="grid gap-1">' +
        '<p class="block m-0">' +
        '<a href="' +
        escapeHtml(product.url) +
        '" class="reversed-link text-pcard-title">' +
        escapeHtml(product.title) +
        '</a>' +
        '</p>' +
        (price ? '<span class="price" style="font-size:0.9em;">' + price + '</span>' : '') +
        '</div>' +
        '</div>'
    );
    return li;
  }

  // ── Full-page fallback ────────────────────────────────────────────────────

  function getNativeTopHandles(limit) {
    const seen = new Set();
    const list = document.getElementById('ProductsList');
    if (!list) return seen;
    for (const a of list.querySelectorAll('a')) {
      if (!a.pathname.startsWith('/products/')) continue;
      const m = a.pathname.match(/\/products\/([^?#/]+)/);
      if (m && m[1] && !seen.has(m[1])) {
        seen.add(m[1]);
        if (seen.size >= limit) break;
      }
    }
    return seen;
  }

  // Pure decision: given the handles already visible to the shopper and a list of
  // title-only search matches (ordered by relevance), return the top title match
  // that should be pinned to the front — or null if there's no title match, or the
  // top one is already visible (native search isn't burying anything for this query).
  function pickBuriedTitleMatch(shownHandles, titleProducts) {
    if (!titleProducts || !titleProducts.length) return null;
    const top = titleProducts[0];
    if (shownHandles.has(top.handle)) return null;
    return top;
  }

  async function initPageFallback() {
    let q = new URLSearchParams(window.location.search).get('q') || '';
    q = q.trim();
    if (!q) return;

    // “my cart” / “cart” typed into search — redirect to cart page
    if (/^(?:my\s+)?cart$/i.test(q)) {
      window.location.replace('/cart');
      return;
    }

    const isEmptyPage = !!document.querySelector('.template-search--empty');

    let result;

    if (!isEmptyPage) {
      const nativeTop = getNativeTopHandles(6);

      // An exact/near-exact TITLE match is the single most relevant result of all,
      // but native search ranks across title+body+vendor+product_type+tag — when the
      // query words are common vocabulary in product body copy (e.g. "book", "lover"
      // appearing across dozens of descriptions), those body/tag mentions can outrank
      // and bury the one product whose TITLE actually contains the phrase. Title
      // matches take priority, so check them first.
      const titleProducts = await suggestProducts(q, MAX_PAGE_RESULTS, 'title');
      let toPin = pickBuriedTitleMatch(nativeTop, titleProducts)
        ? titleProducts.filter(function (p) {
            return !nativeTop.has(p.handle);
          })
        : [];

      if (!toPin.length) {
        // Native search returned results, but may have ranked by title only, burying
        // products whose body/tags contain the exact query term (e.g. “art deco” tag).
        // Search body+tag fields specifically to find the most semantically relevant match.
        const bodyTagProducts = await suggestProducts(q, MAX_PAGE_RESULTS, 'body,tag');
        if (bodyTagProducts.length && !nativeTop.has(bodyTagProducts[0].handle)) {
          toPin = bodyTagProducts.filter(function (p) {
            return !nativeTop.has(p.handle);
          });
        }
      }

      if (!toPin.length) return;

      result = { products: toPin, matched: [q] };
    } else {
      // Empty page: existing full-phrase / word-split logic.
      // Shopify full-page search may miss tag/body matches when options[fields] is
      // absent from the URL (common when arriving via predictive-search click).
      const fullPhraseProducts = await suggestProducts(q, MAX_PAGE_RESULTS);
      result =
        fullPhraseProducts.length > 0
          ? { products: fullPhraseProducts, matched: [q] }
          : await (async function () {
              const words = splitQuery(q);
              if (!words.length) return { products: [], matched: [] };
              return resolveQuery(words);
            })();
    }

    if (!result.products.length) return;

    const header = document.querySelector('.template-search__header');
    if (!header) return;

    // Remove any previous fallback (e.g. back-navigation)
    const existing = document.getElementById('search-fallback');
    if (existing) existing.remove();

    const wrapper = document.createElement('div');
    wrapper.id = 'search-fallback';

    const label = document.createElement('p');
    label.className = 'text-center m-0';
    label.style.cssText = 'font-size:0.88em;opacity:0.6;margin-bottom:20px;letter-spacing:0.01em;';

    if (isEmptyPage) {
      wrapper.style.cssText =
        'padding-top:28px;border-top:1px solid rgba(var(--color-foreground),0.12);';
      const matchedStr = result.matched
        .map(function (w) {
          return '”' + w + '”';
        })
        .join(' and ');
      const isFullPhrase =
        result.matched.length === 1 && result.matched[0].toLowerCase() === q.toLowerCase();
      label.textContent = isFullPhrase
        ? 'Showing results for “' + q + '”'
        : 'No results found for “' + q + '” — showing products for ' + matchedStr + ' instead';
    } else {
      // Dynamic top-match section shown above the native result grid
      wrapper.style.cssText =
        'padding-top:28px;padding-bottom:12px;border-top:1px solid rgba(var(--color-foreground),0.12);';
      label.textContent = 'Top match for “' + q + '”';
    }

    const ul = document.createElement('ul');
    ul.className =
      'grid-list products-list f-grid md:f-grid-3-cols xl:f-grid-4-cols f-grid-2-cols f-grid--gap-medium';
    ul.style.cssText = 'list-style:none;padding:0;margin:0;';
    result.products.forEach(function (p) {
      ul.appendChild(buildPageCard(p));
    });

    wrapper.appendChild(label);

    if (isEmptyPage) {
      const galleryBtn = document.createElement('a');
      galleryBtn.href = '/search?q=' + encodeURIComponent(q) + '&view=museum-gallery';
      galleryBtn.className = 'mg-gallery-btn hidden md:inline-flex';
      galleryBtn.setAttribute('aria-label', 'Switch to museum gallery view');
      safeSetHTML(
        galleryBtn,
        '<svg width=”14” height=”14” viewBox=”0 0 16 16” fill=”none” aria-hidden=”true”>' +
          '<rect x=”1” y=”1” width=”6” height=”6” rx=”0.5” stroke=”currentColor” stroke-width=”1.4”/>' +
          '<rect x=”9” y=”1” width=”6” height=”6” rx=”0.5” stroke=”currentColor” stroke-width=”1.4”/>' +
          '<rect x=”1” y=”9” width=”6” height=”6” rx=”0.5” stroke=”currentColor” stroke-width=”1.4”/>' +
          '<rect x=”9” y=”9” width=”6” height=”6” rx=”0.5” stroke=”currentColor” stroke-width=”1.4”/>' +
          '</svg>' +
          'Gallery View'
      );
      const galleryRow = document.createElement('div');
      galleryRow.style.cssText = 'display:flex;justify-content:flex-end;margin-bottom:12px;';
      galleryRow.appendChild(galleryBtn);
      wrapper.appendChild(galleryRow);
    }

    wrapper.appendChild(ul);
    header.after(wrapper);
  }

  // ── Predictive dropdown fallback ─────────────────────────────────────────

  async function initDropdownFallback(query, resultsContainerId) {
    const words = splitQuery(query);
    if (!words.length) return;

    let container = document.getElementById(resultsContainerId);
    if (!container) return;

    // Tag so a superseding query can cancel this in-flight request
    container.dataset.fallbackQuery = query;

    const result = await resolveQuery(words);
    if (!result.products.length) return;

    // Bail if query changed while we were fetching
    container = document.getElementById(resultsContainerId);
    if (!container || container.dataset.fallbackQuery !== query) return;

    // Don't inject if real results appeared after a re-render
    if (container.querySelector('li, .predictive-search-result')) return;

    const section = document.createElement('div');
    section.className =
      'grid gap-3 predictive-search-result search__box-item search-fallback-dropdown';

    // “No results” notice + heading grouped so they read as one label
    const labelGroup = document.createElement('div');

    const noResultsNote = document.createElement('p');
    noResultsNote.className = 'm-0';
    noResultsNote.style.cssText =
      'font-size:0.75em;opacity:0.5;letter-spacing:0.02em;margin-bottom:2px;';
    noResultsNote.textContent = 'No results for “' + query + '”';

    const heading = document.createElement('h4');
    heading.className = 'h5 predictive-search-result__heading';
    heading.style.cssText = 'margin:0;';
    heading.textContent =
      'Showing instead: ' +
      result.matched
        .map(function (w) {
          return '“' + w + '”';
        })
        .join(', ');

    labelGroup.appendChild(noResultsNote);
    labelGroup.appendChild(heading);

    const ul = document.createElement('ul');
    ul.className = 'grid gap-4';
    ul.setAttribute('role', 'group');

    const shown = result.products.slice(0, MAX_DROPDOWN_RESULTS);
    shown.forEach(function (p, i) {
      ul.appendChild(buildDropdownItem(p, i));
    });

    section.appendChild(labelGroup);
    section.appendChild(ul);
    container.appendChild(section);
  }

  // ── Predictive dropdown — buried title-match pin ─────────────────────────
  // Companion to the full-page burial fix above. Native predictive search
  // returned SOME results (so initDropdownFallback's count===0 trigger never
  // fires), but the dropdown's own tiny result limit (settings.number_results_to_show,
  // default 5) means common-vocabulary body/tag matches can still crowd out the one
  // product whose TITLE actually contains the query phrase. Check for that title
  // match and pin it to the top of the already-rendered list.
  async function pinBuriedTitleMatch(query, resultsContainerId) {
    const container = document.getElementById(resultsContainerId);
    if (!container) return;

    const shown = new Set();
    container.querySelectorAll('a').forEach(function (a) {
      if (!a.pathname || !a.pathname.startsWith('/products/')) return;
      const m = a.pathname.match(/\/products\/([^?#/]+)/);
      if (m && m[1]) shown.add(m[1]);
    });
    if (!shown.size) return;

    // Tag so a superseding query can cancel this in-flight request
    container.dataset.titlePinQuery = query;

    const titleProducts = await suggestProducts(query, 1, 'title');
    const top = pickBuriedTitleMatch(shown, titleProducts);
    if (!top) return;

    // Bail if the query changed while fetching, or a re-render already added it
    const freshContainer = document.getElementById(resultsContainerId);
    if (!freshContainer || freshContainer.dataset.titlePinQuery !== query) return;
    if (freshContainer.querySelector('[data-pinned-handle="' + top.handle + '"]')) return;

    const list = freshContainer.querySelector('ul');
    if (!list) return;

    const li = buildDropdownItem(top, 'pinned');
    li.dataset.pinnedHandle = top.handle;
    list.insertBefore(li, list.firstChild);
  }

  // ── Exports ───────────────────────────────────────────────────────────────

  // CommonJS hook — lets Jest import pure helpers without running browser init.
  // In browser environments `module` is undefined so this block is skipped.
  // eslint-disable-next-line no-undef
  if (typeof module !== 'undefined' && module.exports) {
    // eslint-disable-next-line no-undef
    module.exports = { splitQuery: splitQuery, pickBuriedTitleMatch: pickBuriedTitleMatch };
    return;
  }

  window.SearchFallback = {
    initDropdownFallback: initDropdownFallback,
    pinBuriedTitleMatch: pinBuriedTitleMatch,
  };

  // Run full-page fallback immediately (script is deferred, DOM is ready)
  initPageFallback();
})();
